import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Settings, AlertCircle, CheckCircle, Bell, BellOff, Calendar, Gauge } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function CriticalParameters() {
  const [parameterTypes, setParameterTypes] = useState([]);

  useEffect(() => {
    fetchParameterTypes();
  }, []);

  const fetchParameterTypes = async () => {
    try {
      const response = await axios.get(`${API}/parameter-types`, getAuthHeaders());
      setParameterTypes(response.data);
    } catch (error) {
      console.error('Error fetching parameter types:', error);
      toast.error('Ошибка загрузки типов параметров');
    }
  };

  const handleToggleCritical = async (typeId, currentValue) => {
    try {
      await axios.put(
        `${API}/parameter-types/${typeId}/critical?is_critical=${!currentValue}`,
        null,
        getAuthHeaders()
      );
      
      setParameterTypes(prevTypes =>
        prevTypes.map(pt =>
          pt.id === typeId ? { ...pt, is_critical: !currentValue } : pt
        )
      );
      
      toast.success('Настройка критичности обновлена');
    } catch (error) {
      toast.error('Ошибка обновления настройки');
      console.error('Error updating critical status:', error);
    }
  };

  const handleToggleNotifications = async (typeId, currentValue) => {
    try {
      const paramType = parameterTypes.find(pt => pt.id === typeId);
      
      await axios.put(
        `${API}/parameter-types/${typeId}/notification-settings?notification_enabled=${!currentValue}&notification_days_threshold=${paramType.notification_days_threshold || 10}&notification_km_threshold=${paramType.notification_km_threshold || 100}`,
        null,
        getAuthHeaders()
      );
      
      setParameterTypes(prevTypes =>
        prevTypes.map(pt =>
          pt.id === typeId ? { ...pt, notification_enabled: !currentValue } : pt
        )
      );
      
      toast.success(!currentValue ? 'Уведомления включены' : 'Уведомления выключены');
    } catch (error) {
      toast.error('Ошибка обновления уведомлений');
      console.error('Error updating notification status:', error);
    }
  };

  const handleUpdateThreshold = async (typeId, field, value) => {
    // Validate input
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 0) {
      toast.error('Пожалуйста, введите корректное число');
      return;
    }

    try {
      const paramType = parameterTypes.find(pt => pt.id === typeId);
      
      const params = new URLSearchParams({
        notification_enabled: paramType.notification_enabled ?? true,
        notification_days_threshold: field === 'days' ? numValue : (paramType.notification_days_threshold || 10),
        notification_km_threshold: field === 'km' ? numValue : (paramType.notification_km_threshold || 100),
        notification_repeat_days: field === 'repeat_days' ? numValue : (paramType.notification_repeat_days || 0),
        notification_repeat_km: field === 'repeat_km' ? numValue : (paramType.notification_repeat_km || 0),
      });
      
      await axios.put(
        `${API}/parameter-types/${typeId}/notification-settings?${params.toString()}`,
        null,
        getAuthHeaders()
      );
      
      setParameterTypes(prevTypes =>
        prevTypes.map(pt =>
          pt.id === typeId 
            ? { 
                ...pt, 
                [field === 'days' ? 'notification_days_threshold' : 
                 field === 'km' ? 'notification_km_threshold' :
                 field === 'repeat_days' ? 'notification_repeat_days' :
                 'notification_repeat_km']: numValue 
              } 
            : pt
        )
      );
      
      toast.success(field.includes('repeat') ? 'Периодичность обновлена' : 'Порог уведомлений обновлён');
    } catch (error) {
      toast.error('Ошибка обновления настройки');
      console.error('Error updating threshold:', error);
    }
  };

  const handleChangeTrackingType = async (typeId, newType) => {
    try {
      // Update via notification settings endpoint
      const paramType = parameterTypes.find(pt => pt.id === typeId);
      
      const params = new URLSearchParams({
        notification_enabled: paramType.notification_enabled ?? true,
        notification_days_threshold: paramType.notification_days_threshold || 10,
        notification_km_threshold: paramType.notification_km_threshold || 100,
      });
      
      await axios.put(
        `${API}/parameter-types/${typeId}/notification-settings?${params.toString()}`,
        null,
        getAuthHeaders()
      );
      
      // Update tracking type via direct DB update (we'll need a new endpoint)
      await axios.put(
        `${API}/parameter-types/${typeId}/tracking-type?tracking_type=${newType}`,
        null,
        getAuthHeaders()
      );
      
      setParameterTypes(prevTypes =>
        prevTypes.map(pt =>
          pt.id === typeId ? { ...pt, default_tracking_type: newType } : pt
        )
      );
      
      toast.success(`Тип отслеживания изменён на ${newType === 'date' ? 'дату' : 'пробег'}`);
    } catch (error) {
      toast.error('Ошибка изменения типа отслеживания');
      console.error('Error changing tracking type:', error);
    }
  };

  return (
    <Card className="glass-effect shadow-2xl border-2 border-orange-200 hover:border-orange-400 transition-all">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b-2 border-orange-100">
        <CardTitle className="flex items-center text-2xl">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center mr-3 shadow-lg">
            <Settings className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>
          Настройки параметров и уведомлений
        </CardTitle>
        <CardDescription className="text-base font-medium text-gray-600">
          Настройте критичность и пороги уведомлений для каждого параметра
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {parameterTypes.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Типы параметров не найдены. Создайте их в разделе "Управление типами".
            </p>
          ) : (
            parameterTypes.map((paramType) => (
              <div
                key={paramType.id}
                className="p-5 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200 hover:border-orange-300 transition-all space-y-4"
              >
                {/* Header with parameter name */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-md ${
                      paramType.is_critical 
                        ? 'bg-gradient-to-br from-red-500 to-red-600' 
                        : 'bg-gradient-to-br from-gray-400 to-gray-500'
                    }`}>
                      {paramType.is_critical ? (
                        <AlertCircle className="w-6 h-6 text-white" strokeWidth={2.5} />
                      ) : (
                        <CheckCircle className="w-6 h-6 text-white" strokeWidth={2.5} />
                      )}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-lg">{paramType.name}</div>
                    </div>
                  </div>
                </div>

                {/* Critical Parameter Toggle */}
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className={`w-5 h-5 ${paramType.is_critical ? 'text-red-600' : 'text-gray-400'}`} />
                    <div>
                      <Label className="text-sm font-semibold text-gray-700">Критичный параметр</Label>
                      <p className="text-xs text-gray-500">
                        {paramType.is_critical ? 'Влияет на статус автобуса' : 'Не влияет на статус'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={paramType.is_critical ?? true}
                    onCheckedChange={() => handleToggleCritical(paramType.id, paramType.is_critical ?? true)}
                    className="data-[state=checked]:bg-red-500"
                  />
                </div>

                {/* Notification Toggle */}
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    {(paramType.notification_enabled ?? true) ? (
                      <Bell className="w-5 h-5 text-blue-600" />
                    ) : (
                      <BellOff className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <Label className="text-sm font-semibold text-gray-700">Уведомления в Telegram</Label>
                      <p className="text-xs text-gray-500">
                        {(paramType.notification_enabled ?? true) ? 'Включены' : 'Выключены'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={paramType.notification_enabled ?? true}
                    onCheckedChange={() => handleToggleNotifications(paramType.id, paramType.notification_enabled ?? true)}
                    className="data-[state=checked]:bg-blue-500"
                  />
                </div>

                {/* Notification Thresholds - only show if notifications are enabled */}
                {(paramType.notification_enabled ?? true) && (
                  <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-semibold text-gray-700">Тип отслеживания</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleChangeTrackingType(paramType.id, 'date')}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                            (paramType.default_tracking_type === 'date' || !paramType.default_tracking_type)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          📅 По дате
                        </button>
                        <button
                          onClick={() => handleChangeTrackingType(paramType.id, 'mileage')}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                            paramType.default_tracking_type === 'mileage'
                              ? 'bg-green-500 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          📊 По пробегу
                        </button>
                      </div>
                    </div>
                    
                    {/* Show Days Threshold for date-based parameters */}
                    {(paramType.default_tracking_type === 'date' || !paramType.default_tracking_type) && (
                      <>
                        <div className="flex items-center gap-3">
                          <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1">
                            <Label className="text-xs text-gray-600">За сколько дней до окончания</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                type="number"
                                min="0"
                                value={paramType.notification_days_threshold ?? 10}
                                onChange={(e) => setParameterTypes(prevTypes =>
                                  prevTypes.map(pt =>
                                    pt.id === paramType.id ? { ...pt, notification_days_threshold: parseInt(e.target.value) || 0 } : pt
                                  )
                                )}
                                onBlur={(e) => handleUpdateThreshold(paramType.id, 'days', e.target.value)}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-sm text-gray-600 font-medium">дней</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 pt-2 border-t border-blue-300">
                          <Bell className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <div className="flex-1">
                            <Label className="text-xs text-gray-600">Периодичность повторения (0 = не повторять)</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                type="number"
                                min="0"
                                value={paramType.notification_repeat_days ?? 0}
                                onChange={(e) => setParameterTypes(prevTypes =>
                                  prevTypes.map(pt =>
                                    pt.id === paramType.id ? { ...pt, notification_repeat_days: parseInt(e.target.value) || 0 } : pt
                                  )
                                )}
                                onBlur={(e) => handleUpdateThreshold(paramType.id, 'repeat_days', e.target.value)}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-sm text-gray-600 font-medium">дней</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Show Km Threshold for mileage-based parameters */}
                    {paramType.default_tracking_type === 'mileage' && (
                      <>
                        <div className="flex items-center gap-3">
                          <Gauge className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1">
                            <Label className="text-xs text-gray-600">За сколько км до окончания</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                type="number"
                                min="0"
                                value={paramType.notification_km_threshold ?? 100}
                                onChange={(e) => setParameterTypes(prevTypes =>
                                  prevTypes.map(pt =>
                                    pt.id === paramType.id ? { ...pt, notification_km_threshold: parseInt(e.target.value) || 0 } : pt
                                  )
                                )}
                                onBlur={(e) => handleUpdateThreshold(paramType.id, 'km', e.target.value)}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-sm text-gray-600 font-medium">км</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 pt-2 border-t border-blue-300">
                          <Bell className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <div className="flex-1">
                            <Label className="text-xs text-gray-600">Периодичность повторения (0 = не повторять)</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Input
                                type="number"
                                min="0"
                                value={paramType.notification_repeat_km ?? 0}
                                onChange={(e) => setParameterTypes(prevTypes =>
                                  prevTypes.map(pt =>
                                    pt.id === paramType.id ? { ...pt, notification_repeat_km: parseInt(e.target.value) || 0 } : pt
                                  )
                                )}
                                onBlur={(e) => handleUpdateThreshold(paramType.id, 'repeat_km', e.target.value)}
                                className="w-20 h-8 text-sm"
                              />
                              <span className="text-sm text-gray-600 font-medium">км</span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-xl">ℹ️</span>
            </div>
            <div className="text-sm text-gray-700 space-y-1">
              <p className="font-bold text-gray-900">Как это работает?</p>
              <p>• <strong>Критичный параметр</strong> - просрочка меняет статус автобуса на "Требуется внимание"</p>
              <p>• <strong>Уведомления</strong> - включить/выключить отправку уведомлений в Telegram для этого параметра</p>
              <p>• <strong>Пороги</strong> - за сколько дней/км до окончания начинать присылать уведомления</p>
              <p>• <strong>Периодичность</strong> - как часто повторять уведомления ДО истечения срока (0 = не повторять)</p>
              <p>• <strong>Важно:</strong> Уведомления НЕ отправляются после истечения срока</p>
              <p>• Настройки применяются индивидуально для каждого типа параметра</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
